/**
 * <details>のアニメーション制御を管理
 *
 * 使い方
 * <details>
 *   <summary>開閉ボタン</summary>
 *   <div>コンテンツ1</div>
 *   <div>コンテンツ2</div>
 * </details>
 */

/**
 * 1つのdetailsアニメーションを管理するクラス
 */
export class DetailAnimation {
  private element: HTMLDetailsElement;
  private summary: HTMLElement;
  private contentParent: HTMLElement;
  private animation: Animation | null = null;
  private isAnimating: boolean = false;
  private readonly IS_OPENED_CLASS = 'is-opened';
  private readonly ANIMATION_DURATION = 300;
  private readonly EASING = 'ease-out';
  private readonly ANIMATION_STATUS = 'animStatus';
  private resizeObserver: ResizeObserver;
  private allParentDetails: Set<HTMLDetailsElement> = new Set();

  constructor(element: HTMLDetailsElement) {
    this.element = element;
    this.summary = element.querySelector('summary') as HTMLElement;
    this.contentParent = this.wrapContents();
    this.initParentDetails();
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
    this.init();
  }

  private initParentDetails(): void {
    let parent = this.element.parentElement;
    while (parent) {
      const parentDetails = parent.closest('details');
      if (!parentDetails) break;
      this.allParentDetails.add(parentDetails as HTMLDetailsElement);
      parent = parentDetails.parentElement;
    }
  }

  private wrapContents(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.overflow = 'hidden';

    let currentElement = this.summary.nextElementSibling;
    while (currentElement) {
      const nextElement = currentElement.nextElementSibling;
      wrapper.appendChild(currentElement);
      currentElement = nextElement;
    }

    this.element.appendChild(wrapper);
    return wrapper;
  }

  private init(): void {
    if (!this.summary) return;

    // contentParentにidがなければ自動付与
    if (!this.contentParent.id) {
      this.contentParent.id = `accordion-content-${Math.random().toString(36).substr(2, 9)}`;
    }
    // summaryにaria-controls, aria-expandedを付与
    this.summary.setAttribute('aria-controls', this.contentParent.id);
    this.summary.setAttribute(
      'aria-expanded',
      this.element.hasAttribute('open') ? 'true' : 'false'
    );

    if (!this.element.hasAttribute('open')) {
      this.contentParent.style.height = '0px';
    } else {
      this.contentParent.style.height = `${this.contentParent.scrollHeight}px`;
    }

    this.summary.addEventListener('click', this.handleClick.bind(this));
    this.resizeObserver.observe(this.contentParent);
  }

  private handleResize(entries: ResizeObserverEntry[]): void {
    if (this.isAnimating) return;

    if (this.element.classList.contains(this.IS_OPENED_CLASS)) {
      const currentHeight = this.contentParent.scrollHeight;
      this.contentParent.style.height = `${currentHeight}px`;
    }

    this.updateParentHeights(
      this.element.classList.contains(this.IS_OPENED_CLASS),
      0
    ).catch(console.error);
  }

  private handleClick(event: Event): void {
    event.preventDefault();

    if (
      this.isAnimating ||
      this.element.dataset[this.ANIMATION_STATUS] === 'running'
    ) {
      return;
    }

    this.toggleState();
  }

  public async closeWithAnimation(): Promise<void> {
    if (this.element.classList.contains(this.IS_OPENED_CLASS)) {
      await this.toggleState();
    }
  }

  private async toggleState(): Promise<void> {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.element.dataset[this.ANIMATION_STATUS] = 'running';
    const isOpening = !this.element.classList.contains(this.IS_OPENED_CLASS);
    const startHeight = this.contentParent.offsetHeight;
    const endHeight = isOpening ? this.contentParent.scrollHeight : 0;

    if (isOpening) {
      this.element.setAttribute('open', '');
    }

    // 自身のアニメーションと親要素の更新を同時に行う
    await Promise.all([
      this.animateHeight(startHeight, endHeight),
      this.updateParentHeights(isOpening, endHeight - startHeight),
    ]);

    if (isOpening) {
      this.element.classList.add(this.IS_OPENED_CLASS);
      this.summary.setAttribute('aria-expanded', 'true'); // 開いたらtrue
    } else {
      this.element.classList.remove(this.IS_OPENED_CLASS);
      this.element.removeAttribute('open');
      this.summary.setAttribute('aria-expanded', 'false'); // 閉じたらfalse
    }

    this.contentParent.style.height = isOpening ? 'auto' : '0px';
    this.isAnimating = false;
    delete this.element.dataset[this.ANIMATION_STATUS];
  }

  private async animateHeight(
    startHeight: number,
    endHeight: number
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      const startTime = performance.now();
      const animate = (currentTime: number) => {
        const elapsedTime = currentTime - startTime;
        if (elapsedTime >= this.ANIMATION_DURATION) {
          this.contentParent.style.height = `${endHeight}px`;
          resolve();
          return;
        }

        const progress = elapsedTime / this.ANIMATION_DURATION;
        const easedProgress = this.easeOutCubic(progress);
        const currentHeight =
          startHeight + (endHeight - startHeight) * easedProgress;
        this.contentParent.style.height = `${currentHeight}px`;

        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    });
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private async updateParentHeights(
    isOpening: boolean,
    heightDifference: number
  ): Promise<void> {
    const parentUpdates = Array.from(this.allParentDetails).map(
      async (parentDetails) => {
        const parentAnimation =
          DetailAnimationManager.getInstance().findAnimation(parentDetails);
        if (
          parentAnimation &&
          parentDetails.classList.contains(this.IS_OPENED_CLASS)
        ) {
          const parentWrapper = parentAnimation['contentParent'];
          const currentHeight = parentWrapper.offsetHeight;
          // 修正箇所：heightDifference をそのまま加算
          const newHeight = Math.max(0, currentHeight + heightDifference);

          await parentAnimation.animateHeight(currentHeight, newHeight);
        }
      }
    );

    await Promise.all(parentUpdates);
  }

  public getElement(): HTMLDetailsElement {
    return this.element;
  }

  public destroy(): void {
    this.animation?.cancel();
    this.summary.removeEventListener('click', this.handleClick.bind(this));
    this.resizeObserver.disconnect();
    delete this.element.dataset[this.ANIMATION_STATUS];
  }
}

/**
 * 複数のdetailsアニメーションを管理するマネージャークラス
 */
export class DetailAnimationManager {
  private static instance: DetailAnimationManager | null = null;
  private detailAnimations: DetailAnimation[] = [];

  public static getInstance(): DetailAnimationManager {
    if (!DetailAnimationManager.instance) {
      DetailAnimationManager.instance = new DetailAnimationManager();
    }
    return DetailAnimationManager.instance;
  }

  private constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeDetailAnimations();
      });
    } else {
      this.initializeDetailAnimations();
    }
  }

  private initializeDetailAnimations(): void {
    const details = document.querySelectorAll('details');
    details.forEach((detail) => {
      this.detailAnimations.push(
        new DetailAnimation(detail as HTMLDetailsElement)
      );
    });
  }

  public findAnimation(details: HTMLDetailsElement): DetailAnimation | null {
    return (
      this.detailAnimations.find(
        (animation) => animation.getElement() === details
      ) || null
    );
  }

  public getDetailAnimations(): DetailAnimation[] {
    return this.detailAnimations;
  }

  public destroy(): void {
    this.detailAnimations.forEach((animation) => animation.destroy());
    this.detailAnimations = [];
    DetailAnimationManager.instance = null;
  }

  public addDetailAnimation(details: HTMLDetailsElement): void {
    const existingAnimation = this.findAnimation(details);
    if (!existingAnimation) {
      this.detailAnimations.push(new DetailAnimation(details));
    }
  }

  public removeDetailAnimation(details: HTMLDetailsElement): void {
    const index = this.detailAnimations.findIndex(
      (animation) => animation.getElement() === details
    );
    if (index !== -1) {
      this.detailAnimations[index].destroy();
      this.detailAnimations.splice(index, 1);
    }
  }
}
